import { useCallback } from "react";

import {
  paywallCheckoutAttemptsApi,
  type ProcessorError,
} from "@/react/api/paywallCheckoutAttemptsApi";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";

import { CheckoutError } from "../../CheckoutError";
import type { MemberAddressState } from "../types";
import type { ConfirmPaymentParams } from "./useConfirmPayment";
import type { Attempt } from "./useCreateAttempt";
import { useResolvePaymentRecoveryAction } from "./useResolvePaymentRecoveryAction";

type ConfirmPaymentFn = (params: ConfirmPaymentParams) => Promise<boolean>;
type ResubmitElementsBeforeRetryFn = () => Promise<void>;

interface UseRecoverPaymentParams {
  confirmPayment: ConfirmPaymentFn;
  paymentMethodType: string;
  resubmitElementsBeforeRetry?: ResubmitElementsBeforeRetryFn;
}

const buildProcessorError = (error: CheckoutError): ProcessorError => {
  if (error.isStripeError()) {
    return {
      message: error.message,
      code: error.context.code,
      decline_code: error.context.decline_code,
    };
  }
  if (error.isNewPaywallError()) {
    const metadata = error.context.failure.metadata;
    return {
      message: error.message,
      // `?? undefined` intentionally converts null to undefined: the Ruby serializer
      // emits null for missing keys, but the backend expects the field to be absent
      // (undefined), not explicitly null.
      code: metadata?.code ?? undefined,
      decline_code: metadata?.decline_code ?? undefined,
    };
  }
  return { message: error.message };
};

/**
 * Orchestrates the full payment error recovery flow:
 *   1. Check PI status to determine action (navigate / retry / cancel)
 *   2. If 'navigate': return { shouldNavigate: true }
 *   3. If 'retry': attempt confirmPayment once more
 *   4. Cancel the attempt, passing the resolved processor error
 *
 * Returns { shouldNavigate: true } when the caller should navigate to verification,
 * or { shouldNavigate: false, error } when the attempt was cancelled and the error
 * should be surfaced to the user.
 */
export const useRecoverPayment = ({
  confirmPayment,
  paymentMethodType,
  resubmitElementsBeforeRetry,
}: UseRecoverPaymentParams) => {
  const resolvePaymentRecoveryAction = useResolvePaymentRecoveryAction();

  return useCallback(
    async (
      error: CheckoutError,
      attempt: Attempt,
      billingMemberAddress: MemberAddressState | undefined,
      returnUrl: string | undefined,
    ): Promise<
      { shouldNavigate: true } | { shouldNavigate: false; error: CheckoutError }
    > => {
      const action = await resolvePaymentRecoveryAction(attempt);

      // Payment already completed on Stripe — skip cancel and let the
      // payment_intent.succeeded webhook complete the attempt server-side.
      if (action === "navigate") return { shouldNavigate: true };

      let resolvedError = buildProcessorError(error);

      // paymentIntentClientSecret guard is handled upstream (usePaymentRecoveryAction
      // returns 'cancel' when it is missing); billingMemberAddress / returnUrl are
      // checked here for TypeScript narrowing only — in practice they are always set
      // when the caller reaches this point.
      if (
        action === "retry" &&
        attempt.paymentIntentClientSecret &&
        billingMemberAddress &&
        returnUrl
      ) {
        try {
          await resubmitElementsBeforeRetry?.();
          await confirmPayment({
            memberAddress: billingMemberAddress,
            paymentIntentClientSecret: attempt.paymentIntentClientSecret,
            paymentMethodType,
            returnUrl,
          });
          return { shouldNavigate: true };
        } catch (retryError) {
          const retryCheckoutError = CheckoutError.fromUnknownError(
            "useRecoverPayment.retry",
            retryError,
          );
          resolvedError = buildProcessorError(retryCheckoutError);
          notifyBugsnag(
            retryError instanceof Error
              ? retryError
              : new Error("useRecoverPayment: confirmPayment retry failed"),
          );
        }
      }

      // If createAttempt succeeded but a later step threw, notify the backend so it
      // can cancel the attempt and reset the session step to failed. This unblocks
      // retry -- without this the step stays in `processing` and subsequent checkout
      // attempts return `session_expired`.
      // Best-effort: if the cancel call itself fails, swallow the error so the
      // original error is still surfaced to the user.
      try {
        const cancelResponse = await paywallCheckoutAttemptsApi.cancel(
          attempt.id,
          resolvedError,
        );
        const { failure } = cancelResponse;
        if (cancelResponse.navigate || !failure) {
          return { shouldNavigate: true };
        }
        return {
          shouldNavigate: false,
          error: CheckoutError.fromPaywallError(
            "useRecoverPayment.cancelAttempt",
            { ...cancelResponse, failure },
          ),
        };
      } catch {
        // Best-effort — don't mask the original error
      }

      return { shouldNavigate: false, error };
    },
    [
      confirmPayment,
      paymentMethodType,
      resolvePaymentRecoveryAction,
      resubmitElementsBeforeRetry,
    ],
  );
};
